import { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
// oauth-validation is used by OAuthCallback.tsx, not needed here anymore

const THEME_STORAGE_KEY = "adigo-theme";

export type AppRole = "user" | "admin" | "analyst" | "adigig" | "super_admin";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole>("user");
  const [loading, setLoading] = useState(true);

  // Prevent double-processing in React.StrictMode and avoid state updates during hard redirects.
  const isRedirectingRef = useRef(false);

  // Update session_started_at on sign in for accurate max lifetime tracking
  // Per Security Audit remediation - provides exact session start timestamp
  const updateSessionStartedAt = useCallback(async (userId: string) => {
    try {
      await supabase
        .from("profiles")
        .update({ session_started_at: new Date().toISOString() })
        .eq("id", userId);
    } catch (error) {
      console.error("Failed to update session_started_at:", error);
    }
  }, []);

  const isGoogleOAuthSession = (s: Session | null) => {
    const u = s?.user;
    if (!u) return false;
    return (
      u.app_metadata?.provider === "google" ||
      u.identities?.some((i) => i.provider === "google")
    );
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        console.log("[AuthContext] onAuthStateChange:", event, "path:", window.location.pathname, "isGoogle:", isGoogleOAuthSession(nextSession), "userId:", nextSession?.user?.id?.slice(0, 8));
        if (isRedirectingRef.current) return;

        // TOKEN_REFRESHED must NOT gate the app — it would unmount all
        // protected pages (including the CMS editor) and destroy unsaved state.
        if (event === "TOKEN_REFRESHED") {
          if (nextSession) {
            setSession(nextSession);
            setUser(nextSession.user);
          }
          return;
        }

        // Keep app gated until we're sure what to do.
        setLoading(true);

        if (nextSession?.user) {
          // For Google OAuth sign-ins, accept session and let ProtectedRoute + 
          // useIsActivatedMember handle activation gating (fail-closed).
          // OAuthCallback handles routing for new vs existing users.
          if (event === "SIGNED_IN" && isGoogleOAuthSession(nextSession)) {
            console.log("[AuthContext] Google OAuth SIGNED_IN — accepting session");
            setSession(nextSession);
            setUser(nextSession.user);

            // Track session start for max lifetime enforcement (only if profile exists)
            void updateSessionStartedAt(nextSession.user.id);
            localStorage.setItem(THEME_STORAGE_KEY, "light");
            document.documentElement.classList.remove("dark");
            document.documentElement.classList.add("light");

            // Check if this was an onboarding OAuth signup
            const ssIntent = sessionStorage.getItem("oauth_intent");
            const ssNext = sessionStorage.getItem("oauth_next");
            const lsPending = localStorage.getItem("onboarding_oauth_pending");
            const onboardingTarget = ssNext || "/onboarding/profile?oauth=success";

            if (ssIntent === "onboarding" || lsPending) {
              sessionStorage.removeItem("oauth_intent");
              sessionStorage.removeItem("oauth_next");
              localStorage.removeItem("onboarding_oauth_pending");
              console.log("[AuthContext] Onboarding OAuth intent — redirecting to:", onboardingTarget);
              window.location.replace(onboardingTarget);
              isRedirectingRef.current = true;
              return;
            }

            // Resolve role before unblocking the app
            setTimeout(() => {
              void (async () => {
                const { data: userRole } = await supabase.rpc("get_user_role", {
                  _user_id: nextSession.user.id,
                });
                setRole((userRole as AppRole) || "user");
                setLoading(false);
              })();
            }, 0);
            return;
          }

          // Non-OAuth (or non SIGNED_IN) session: proceed normally
          setSession(nextSession);
          setUser(nextSession.user);

          if (event === "SIGNED_IN") {
            // Track session start for max lifetime enforcement
            void updateSessionStartedAt(nextSession.user.id);
            localStorage.setItem(THEME_STORAGE_KEY, "light");
            document.documentElement.classList.remove("dark");
            document.documentElement.classList.add("light");
          }

          // IMPORTANT: keep the app gated until role is resolved to avoid routing to /home by default.
          setTimeout(() => {
            void (async () => {
              const { data: userRole } = await supabase.rpc("get_user_role", {
                _user_id: nextSession.user.id,
              });
              setRole((userRole as AppRole) || "user");
              setLoading(false);
            })();
          }, 0);
          return;
        }

        // Signed out
        setSession(null);
        setUser(null);
        setRole("user");
        setLoading(false);
      }
    );

    // THEN check for existing session
    setLoading(true);
    supabase.auth.getSession().then(async ({ data: { session: initialSession } }) => {
      console.log("[AuthContext] getSession:", "isGoogle:", isGoogleOAuthSession(initialSession), "path:", window.location.pathname, "userId:", initialSession?.user?.id?.slice(0, 8));
      if (isRedirectingRef.current) return;

      // Validate session server-side to catch deleted/invalid accounts
      if (initialSession) {
        const { data: { user: validatedUser }, error: validateErr } = await supabase.auth.getUser();
        if (validateErr || !validatedUser) {
          console.warn("[AuthContext] Session invalid (user deleted or token expired) — signing out");
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
          setRole("user");
          setLoading(false);
          return;
        }
      }

      // For persisted Google OAuth sessions, skip waitlist validation.
      // Just accept the session and resolve the role normally.
      if (isGoogleOAuthSession(initialSession)) {
        console.log("[AuthContext] getSession: persisted Google session — accepting without waitlist guard");
        setSession(initialSession);
        setUser(initialSession!.user);

        const userId = initialSession!.user.id;
        supabase.rpc("get_user_role", { _user_id: userId }).then(({ data: userRole }) => {
          setRole((userRole as AppRole) || "user");
          setLoading(false);
        });
        return;
      }

      setSession(initialSession);
      setUser(initialSession?.user ?? null);

      if (initialSession?.user) {
        supabase
          .rpc("get_user_role", { _user_id: initialSession.user.id })
          .then(({ data: userRole }) => {
            if (userRole) setRole(userRole as AppRole);
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole("user");
  };

  return (
    <AuthContext.Provider value={{ user, session, role, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
